/*eslint-disable */
import { Box, Card, Container, Grid, Typography, styled, useTheme } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import AddSharpIcon from "@mui/icons-material/AddSharp";
import WelcomeCard from "./WelcomeCard";
import DragDropList from "./DragDropList";
import { useAdvanceBlogContent, useAdvanceSubtopicBlogContent } from "../../hooks/utils";
import ShowAdvanceContent from "./ShowAdvanceContent";
import DraggableItemsLoader from "./DraggableItemsLoader";

const SectionWrapper = styled("section")(({ theme }) => ({
  minHeight: "calc(100vh - 70px)",
  display: "flex",
  justifyContent: "center",
  backgroundColor: theme.palette.background.default,
  padding: "0 0 30px",
  "& .SyncIconWrapper": {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    "& svg": {
      color: theme.palette.primary.main,
      width: "70%",
      height: "auto",
      "@media screen and (max-width:900px)": {
        transform: "rotate(90deg)",
        width: "45px",
      },
    },
  },
  "& .TopicGenerateBoxTwo": {
    "& .TopicsWrapper": {
      border: "1px solid" + theme.palette.primary.main,
      borderRadius: "10px",
      marginBottom: "15px",
      padding: "13px 18px 14px",
      position: "relative",
      "&:last-of-type": { marginBottom: "0" },
      "& h4": {
        fontSize: "16px",
        fontWeight: 700,
      },
      "& .TopIconBarWrapper": {
        position: "absolute",
        top: "0",
        left: "0",
        width: "100%",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "flex-end",
        padding: "0 10px",
        opacity: "0",
        "& .PlusIcon": {
          backgroundColor: theme.palette.background.default,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          "& svg": {
            fontSize: "24px",
            cursor: "pointer",
          },
        },
      },
      "&:hover": {
        "& .TopIconBarWrapper": { opacity: "1" },
      },
    },
  },
  "& .BoxLoaderWrapper": {
    position: "absolute",
    top: "0",
    left: "0",
    width: "100%",
    height: "100%",
    backgroundColor: theme.palette.background.default + "9",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  "& .MargeContentWrapper": {
    "& .CopyWrapper": {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: "22px",
      "& p": {
        color: theme.palette.primary.main,
        fontWeight: "600",
      },
    },
    "& .ContentWrapper": {
      marginBottom: "20px",
      "&:last-child": { marginBottom: "0px" },
      "& h2": {
        fontSize: "16px",
        fontWeight: 700,
        marginBottom: "8px",
      },
      "& p": { fontSize: "14px" },
      "& .TabLabelWrapper": {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "10px",
        marginBottom: "35px",
        "@media screen and (max-width:600px)": {
          flexDirection: "column-reverse",
        },
        "& button.MuiTab-root": {
          "&:focus": {
            outline: "none",
          },
        },
        "& .PublishBtnWrapper": {
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: "10px",
        },
      },
    },
  },
}));

function AdvanceOption({
  loading = false,
  setLoading = () => false,
  advanceSubtopicData = () => null,
  projectId,
  title = "",
  items = [],
  setItems = () => null,
  isDisable = false,
  setIsDisable = () => false,
  generatedItems = [],
  titleAndMetaDescription,
  setGeneratedItems = () => false,
  isLoading = false,
  content = [],
}) {
  const theme = useTheme();
  const customizer = useSelector(state => state.customizer);
  const [collapse, setCollapse] = useState(false);
  const [collapseHide, setCollapseHide] = useState(false);
  const [showCompleteContent, setShowCompleteContent] = useState(false);
  const [deletedItems, setDeletedItems] = useState([]);
  const [comLoading, setComLoading] = useState(true);
  const contentRef = useRef(null);

  const handleCollapse = () => {
    setCollapse(prev => !prev);
    if (collapseHide) {
      setTimeout(() => {
        setCollapseHide(prev => !prev);
      }, 500);
    } else {
      setCollapseHide(prev => !prev);
    }
  };

  const handleDelete = deletedItem => {
    const isDuplicate = deletedItems.includes(deletedItem.Topic);

    if (!isDuplicate) {
      setDeletedItems(prevItems => [...prevItems, deletedItem.Topic]);
    } else {
      setDeletedItems(prevItems => prevItems.map(item => (item === deletedItem.Topic ? deletedItem.Topic : item)));
    }

    setItems(prevItems => prevItems.filter(item => item.id !== deletedItem.id));
    setGeneratedItems(prevItems => prevItems.filter(item => item !== deletedItem.Topic));
  };

  const handleCompleteButtonClick = async () => {
    setComLoading(false);
    const topics = items?.map(item => item.Topic).join(",");
    await advanceSubtopicData(topics, true);
    setShowCompleteContent(true);
    scrollToContent();
  };

  const scrollToContent = () => {
    if (contentRef?.current) {
      contentRef?.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleAddSubtopic = async item => {
    setLoading(true);
    await advanceSubtopicData(item);
    setLoading(false);
  };

  useEffect(() => {
    if (showCompleteContent) {
      scrollToContent();
    }
  }, [showCompleteContent]);

  return (
    <SectionWrapper>
      <Container maxWidth="">
        <Box sx={{ flexGrow: 1 }}>
          <Grid container spacing={0} mt={0} className="welcomeCardBox">
            <Grid item xs={12} md={12}>
              <WelcomeCard />
            </Grid>
          </Grid>
          <Grid container spacing={2} mt={0}>
            <Grid item xs={12} md={!collapse ? 7 : 12} sx={{ transition: "all 0.5s ease-in-out" }}>
              <Card
                sx={{
                  padding: { xs: "15px 15px 15px 15px", sm: "40px 45px 40px 40px" },
                  border: `1px solid ${theme.palette.divider}`,
                  height: "100%",
                  position: "relative",
                  boxShadow: "none",
                }}
              >
                <DragDropList
                  collapse={collapse}
                  loading={loading}
                  setIsDisable={setIsDisable}
                  handleCollapse={handleCollapse}
                  advanceSubtopicData={advanceSubtopicData}
                  items={items}
                  setItems={setItems}
                  isDisable={isDisable}
                  handleCompleteButtonClick={handleCompleteButtonClick}
                  setGeneratedItems={setGeneratedItems}
                  handleDelete={handleDelete}
                  comLoading={comLoading}
                />
              </Card>
            </Grid>

            {!collapseHide && (
              <>
                <Grid item xs={12} md={0.5} sx={{ transition: "all 0.5s ease-in-out" }}>
                  <Box className="SyncIconWrapper">
                    <SyncAltIcon />
                  </Box>
                </Grid>
                <Grid item xs={12} md={4.5} sx={{ transition: "all 0.5s ease-in-out" }}>
                  <Card
                    sx={{
                      padding: { xs: "15px 15px 50px 15px", sm: "40px 45px 40px 40px" },
                      border: `1px solid ${theme.palette.divider}`,
                      height: "100%",
                      position: "relative",
                      boxShadow: "none",
                    }}
                  >
                    {isLoading ? (
                      Array.from({ length: 5 }).map((_, index) => <DraggableItemsLoader key={index} Topic={true} TopicTwo={true} />)
                    ) : (
                      <Box className="TopicGenerateBoxTwo">
                        {content &&
                          content?.map(item => (
                            <Box className="TopicsWrapper" key={item.id}>
                              <Typography component="h4">{item}</Typography>
                              <Box className="TopIconBarWrapper">
                                {loading ||
                                (generatedItems.includes(item) && !deletedItems.includes(item)) ||
                                (generatedItems.includes(item) && generatedItems.some(generatedItem => generatedItem === item)) ? (
                                  <Box className="PlusIcon">
                                    <AddSharpIcon style={{ cursor: "not-allowed", opacity: 0.5 }} />
                                  </Box>
                                ) : (
                                  <Box className="PlusIcon">
                                    <AddSharpIcon onClick={() => handleAddSubtopic(item)} />
                                  </Box>
                                )}
                              </Box>
                            </Box>
                          ))}
                      </Box>
                    )}
                  </Card>
                </Grid>
              </>
            )}
            {showCompleteContent > 0 && (
              <Grid item xs={12} md={12} sx={{ transition: "all 0.5s ease-in-out" }} className="showCompleteContent" ref={contentRef}>
                <Card
                  sx={{
                    padding: { xs: "15px 15px 50px 15px", sm: "40px 45px 40px 40px" },
                    border: `1px solid ${theme.palette.divider}`,
                    height: "100%",
                    position: "relative",
                  }}
                  elevation={customizer.isCardShadow ? 9 : 0}
                  variant={!customizer.isCardShadow ? "outlined" : undefined}
                >
                  {showCompleteContent && <ShowAdvanceContent title={title} projectId={projectId} items={items} titleAndMetaDescription={titleAndMetaDescription} />}
                </Card>
              </Grid>
            )}
          </Grid>
        </Box>
      </Container>
    </SectionWrapper>
  );
}

export default AdvanceOption;
